import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, CircularProgress, Alert, Typography, Paper, Fab, Container, Tabs, Tab } from '@mui/material';
import { Add as AddIcon, BugReport as BugIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { BugReportList } from '../components/bug-report/BugReportList';
import { BugReportForm } from '../components/bug-report/BugReportForm'; 
import { BugReport, BugReportFormData } from '../types/bugReport';
import { bugReportService } from '../services/bugReportService';

const BugReports: React.FC = () => {
  const [bugReports, setBugReports] = useState<BugReport[]>([]);
  const [filteredReports, setFilteredReports] = useState<BugReport[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<BugReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Load data on component mount
  useEffect(() => {
    loadBugReports();
  }, []);

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Update filtered reports when bugReports or statusFilter changes
  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredReports(bugReports);
    } else {
      setFilteredReports(bugReports.filter(report => report.status === statusFilter));
    }
  }, [bugReports, statusFilter]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadBugReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const reports = await bugReportService.getAll();
      setBugReports(reports);
    } catch (err) {
      console.error('Failed to load bug reports:', err);
      setError('Hata raporları yüklenirken bir sorun oluştu. Lütfen sayfayı yenileyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async (formData: BugReportFormData, file?: File) => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);
      await bugReportService.create(formData, file);
      setIsFormOpen(false);
      setSuccessMessage('Hata raporu başarıyla oluşturuldu.');
      scrollToTop();
      // Reload the list to get the latest data from server
      await loadBugReports();
    } catch (err) {
      console.error('Failed to create bug report:', err);
      setError('Hata raporu oluşturulurken bir sorun oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditReport = (report: BugReport) => {
    setSelectedReport(report);
    setIsFormOpen(true);
    // Clear any existing messages when opening edit dialog
    setError(null);
    setSuccessMessage(null);
  };

  // Delete functionality is commented out since it's not implemented in the backend
  /*
  const handleDeleteReport = async (reportId: string) => {
    try {
      setError(null);
      await bugReportService.delete(reportId);
      setBugReports(bugReports.filter(report => report.id !== reportId));
    } catch (err) {
      console.error('Failed to delete bug report:', err);
      setError('Hata raporu silinirken bir sorun oluştu. Lütfen tekrar deneyin.');
    }
  };
  */

  const handleUpdateReport = async (formData: BugReportFormData, file?: File) => {
    if (!selectedReport) return;
    
    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);
      await bugReportService.update(selectedReport.id, formData, file);
      setSelectedReport(null);
      setIsFormOpen(false);
      setSuccessMessage('Hata raporu başarıyla güncellendi.');
      scrollToTop();
      // Reload the list to get the latest data from server
      await loadBugReports();
    } catch (err) {
      console.error('Failed to update bug report:', err);
      setError('Hata raporu güncellenirken bir sorun oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedReport(null);
    // Clear any form-related errors when closing
    if (error && error.includes('raporu')) {
      setError(null);
    }
  };

  const handleRetry = () => {
    loadBugReports();
  };

  const handleNewReportClick = () => {
    setIsFormOpen(true);
    // Clear any existing messages when opening new report dialog
    setError(null);
    setSuccessMessage(null);
  };

  const handleStatusFilterChange = (event: React.SyntheticEvent, newValue: string) => {
    setStatusFilter(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ color: '#1e3a8a', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Hata raporları yükleniyor...
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: '#fff'
    }}>
      {/* Header Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e40af 100%)',
        color: '#ffffff',
        py: 6,
        mb: 4
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <BugIcon sx={{ fontSize: 32, color: '#ffffff' }} />
              </Box>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#ffffff' }}>
                  Hata Raporları
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, color: '#ffffff' }}>
                  Sistem hatalarını takip edin ve yönetin
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={handleRetry}
                disabled={loading}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: '#ffffff',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.2)'
                  }
                }}
              >
                Yenile
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewReportClick}
                disabled={submitting}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  color: '#ffffff',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)'
                  }
                }}
              >
                Yeni Hata Raporu
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Alert Messages */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontWeight: 500
              }
            }}
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                Tekrar Dene
              </Button>
            }
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontWeight: 500
              }
            }}
            onClose={() => setSuccessMessage(null)}
          >
            {successMessage}
          </Alert>
        )}

        {/* Stats Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
              {bugReports.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Toplam Rapor
            </Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#dc2626', mb: 1 }}>
              {bugReports.filter(r => r.status === 'open').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Açık Hatalar
            </Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#d97706', mb: 1 }}>
              {bugReports.filter(r => r.status === 'in-progress').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Devam Eden
            </Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#059669', mb: 1 }}>
              {bugReports.filter(r => r.status === 'resolved').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Çözülen
            </Typography>
          </Paper>
        </Box>

        {/* Status Tabs */}
        <Box sx={{ mb: 3 }}>
          <Tabs 
            value={statusFilter} 
            onChange={handleStatusFilterChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 120,
                px: 3,
              },
              '& .Mui-selected': {
                color: '#1e3a8a',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1e3a8a',
              },
            }}
          >
            <Tab 
              label={`Tümü (${bugReports.length})`} 
              value="all"
            />
            <Tab 
              label={`Açık (${bugReports.filter(r => r.status === 'open').length})`} 
              value="open"
            />
            <Tab 
              label={`Devam Eden (${bugReports.filter(r => r.status === 'in-progress').length})`} 
              value="in-progress"
            />
            <Tab 
              label={`Çözülen (${bugReports.filter(r => r.status === 'resolved').length})`} 
              value="resolved"
            />
          </Tabs>
        </Box>

        {/* Main Content */}
        {filteredReports.length === 0 && !loading ? (
          <Paper elevation={0} sx={{ 
            p: 8, 
            textAlign: 'center', 
            borderRadius: 3,
            border: '1px solid #e2e8f0'
          }}>
            <BugIcon sx={{ fontSize: 64, color: '#94a3b8', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
              {statusFilter === 'all' 
                ? 'Henüz hata raporu bulunmuyor'
                : 'Bu durumda hata raporu bulunmuyor'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {statusFilter === 'all'
                ? 'İlk hata raporunuzu oluşturmak için aşağıdaki butona tıklayın'
                : 'Farklı bir durum seçin veya yeni bir hata raporu oluşturun'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewReportClick}
              sx={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #ffffff 100%)',
                color: '#1e3a8a',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                '&:hover': {
                  background: 'linear-gradient(135deg, #1e40af 0%, #f8fafc 100%)'
                }
              }}
            >
              Yeni Hata Raporu
            </Button>
          </Paper>
        ) : (
          <Paper elevation={0} sx={{ 
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}>
            <BugReportList 
              bugReports={filteredReports} 
              onEdit={handleEditReport}
            />
          </Paper>
        )}
      </Container>

      {/* Form Dialog */}
      <Dialog
        open={isFormOpen}
        onClose={handleFormClose}

        fullWidth
        disableEscapeKeyDown={submitting}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            '& .MuiDialogContent-root': {
              padding: 0
            }
          }
        }}
      >
        <BugReportForm
          onSubmit={selectedReport ? handleUpdateReport : handleCreateReport}
          onCancel={submitting ? undefined : handleFormClose}
          initialData={selectedReport}
        />
        {submitting && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            backgroundColor: 'rgba(255,255,255,0.8)', 
            zIndex: 1000,
            borderRadius: 3
          }}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
              <CircularProgress size={32} sx={{ color: '#1e3a8a', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {selectedReport ? 'Güncelleniyor...' : 'Kaydediliyor...'}
              </Typography>
            </Paper>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default BugReports; 